import { LayerPanel } from "./LayerPanel";
import { ScenarioPanel } from "./scenario/ScenarioPanel";
import { CostPanel } from "./finance/CostPanel";
import { DemographicsPanel } from "./demographics/DemographicsPanel";
import { RisksPanel } from "./risks/RisksPanel";
import { ClimatePanel } from "./climate/ClimatePanel";
import { StatsPanel } from "./stats/StatsPanel";
import { LevelControl } from "./LevelControl";
import {
  CompassIcon,
  EuroIcon,
  HazardIcon,
  LogoutIcon,
  PeopleIcon,
  ScatterIcon,
  ThermometerIcon,
  TrendIcon,
} from "./icons";
import { useAuthStore } from "../state/useAuthStore";
import { useUiStore, type TabId } from "../state/useUiStore";

/**
 * The app's left rail.
 *
 * Docked rather than floating over the map: the panes here are worked in, not
 * glanced at, and a scenario with inputs, figures and a chart needs room that
 * a floating card cannot give without covering the map it refers to.
 *
 * Two columns. The narrow one is the pane switcher and nothing else — seven
 * labels stopped fitting across the top long ago, and wrapping them cost two
 * rows of header on every pane. Running them down the side gives each one an
 * icon and a fixed place, and the width it costs is the width the wrapped row
 * was already using. Tabs stay the growth seam: another pane is another entry
 * in `TABS`.
 */
const TABS: {
  id: TabId;
  label: string;
  icon: (p: { className?: string }) => React.ReactElement;
}[] = [
  { id: "explore", label: "Explore", icon: CompassIcon },
  { id: "scenario", label: "Scenario", icon: TrendIcon },
  { id: "people", label: "People", icon: PeopleIcon },
  { id: "risks", label: "Risks", icon: HazardIcon },
  { id: "climate", label: "Climate", icon: ThermometerIcon },
  { id: "stats", label: "Stats", icon: ScatterIcon },
  { id: "cost", label: "Cost", icon: EuroIcon },
];

export function Sidebar() {
  // Lifted into a store once the Climate pane needed to send the reader to
  // Risks for flooding rather than duplicating it — see `useUiStore`.
  const tab = useUiStore((s) => s.tab);
  const setTab = useUiStore((s) => s.setTab);
  const logout = useAuthStore((s) => s.logout);
  const active = TABS.find((t) => t.id === tab) ?? TABS[0];

  return (
    <aside className="z-20 flex h-full w-[25rem] shrink-0 border-r border-line bg-surface">
      <nav
        className="flex w-14 shrink-0 flex-col items-center border-r border-line bg-surface-alt py-2"
        aria-label="Panels"
      >
        <img
          src="/logo.png"
          alt="meteo.immo"
          title="meteo.immo"
          width={22}
          height={22}
          className="mb-1.5 shrink-0"
        />
        <div className="mb-1 h-px w-6 bg-line" />
        {TABS.map((t) => {
          const on = tab === t.id;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              aria-current={on ? "page" : undefined}
              className={`relative flex w-full flex-col items-center gap-1 py-2 text-[9px] font-medium leading-none transition-colors ${
                on ? "text-accent" : "text-ink-soft hover:text-ink"
              }`}
            >
              {/* The rail's answer to the underline the top tabs used: the
                  accent has to sit on an edge, and this is the edge. */}
              <span
                className={`absolute bottom-1 left-0 top-1 w-0.5 rounded-r ${
                  on ? "bg-accent" : "bg-transparent"
                }`}
              />
              <Icon className="h-5 w-5" />
              {t.label}
            </button>
          );
        })}
        <button
          onClick={() => logout()}
          title="End this session"
          className="mt-auto flex w-full flex-col items-center gap-1 py-2 text-[9px] font-medium leading-none text-ink-soft transition-colors hover:text-ink"
        >
          <LogoutIcon className="h-5 w-5" />
          Log out
        </button>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        {/*
          The header belongs to every pane, not to Explore. Which geography
          the map is grouped over decides what a click selects, so it decides
          what every other pane is looking at — it used to sit inside the layer
          list, where changing the unit of the whole app meant going back to
          one tab to do it.
        */}
        {/* Tinted like the rail: what is always on screen is chrome, and
            keeping it one band stops the grouping control reading as the
            first row of whichever pane happens to be open. */}
        <header className="border-b border-line bg-surface-alt px-4 pb-2.5 pt-3">
          <h2 className="text-sm font-semibold tracking-tight">{active.label}</h2>
          <LevelControl />
        </header>

        {/*
          Every pane stays mounted and all but one is hidden, rather than
          switching on the tab. Remounting would drop scroll position and
          re-run the layer list's effects every time the user looks at a
          scenario and comes back.
        */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div hidden={tab !== "explore"}>
            <LayerPanel />
          </div>
          <div hidden={tab !== "scenario"}>
            <ScenarioPanel visible={tab === "scenario"} />
          </div>
          <div hidden={tab !== "people"}>
            <DemographicsPanel visible={tab === "people"} />
          </div>
          <div hidden={tab !== "risks"}>
            <RisksPanel visible={tab === "risks"} />
          </div>
          <div hidden={tab !== "climate"}>
            <ClimatePanel visible={tab === "climate"} />
          </div>
          <div hidden={tab !== "stats"}>
            <StatsPanel visible={tab === "stats"} />
          </div>
          <div hidden={tab !== "cost"}>
            <CostPanel visible={tab === "cost"} />
          </div>
        </div>
      </div>
    </aside>
  );
}
